import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Alert,
  Chip,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  FileCopy as CloneIcon,
  BarChart as ChartIcon,
  FilePresent as PreviewIcon
} from '@mui/icons-material';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';

const BillDatabase = ({ onDuplicateBill }) => {
  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [companies, setCompanies] = useState({});

  // Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedGst, setSelectedGst] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog Preview State
  const [previewUrl, setPreviewUrl] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);

  useEffect(() => {
    fetchBills();
    fetchCompaniesMap();
  }, []);

  useEffect(() => {
    applyFilters();
    setPage(0); // Reset page on filter change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bills, searchQuery, selectedCompany, selectedGst, startDate, endDate]);

  const fetchCompaniesMap = async () => {
    try {
      const { data } = await supabase.from('companies').select('id, name');
      const map = {};
      data?.forEach(c => {
        map[c.id] = c.name;
      });
      setCompanies(map);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchBills = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('bills')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBills(data || []);
    } catch (error) {
      console.error('Error fetching bills:', error);
      setAlert({ show: true, message: 'Failed to retrieve bills registry', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickFilter = (type) => {
    const now = new Date();
    const formatDateStr = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (type) {
      case 'this_month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setStartDate(formatDateStr(start));
        setEndDate(formatDateStr(end));
        break;
      }
      case 'this_quarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        const startMonth = quarter * 3;
        const start = new Date(now.getFullYear(), startMonth, 1);
        const end = new Date(now.getFullYear(), startMonth + 3, 0);
        setStartDate(formatDateStr(start));
        setEndDate(formatDateStr(end));
        break;
      }
      case 'this_fy': {
        // Indian FY runs April 1st to March 31st
        let startYear = now.getFullYear();
        if (now.getMonth() < 3) { // Jan, Feb, Mar (0, 1, 2)
          startYear = now.getFullYear() - 1;
        }
        const start = new Date(startYear, 3, 1);
        const end = new Date(startYear + 1, 2, 31);
        setStartDate(formatDateStr(start));
        setEndDate(formatDateStr(end));
        break;
      }
      case 'all_time':
      default:
        setStartDate('');
        setEndDate('');
        break;
    }
  };

  const applyFilters = () => {
    let result = [...bills];

    // Text Search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(bill => 
        bill.bill_number?.toLowerCase().includes(query) ||
        bill.customer_name?.toLowerCase().includes(query)
      );
    }

    // Company Filter
    if (selectedCompany !== 'all') {
      result = result.filter(bill => bill.company_id === selectedCompany);
    }

    // GST Filter
    if (selectedGst !== 'all') {
      result = result.filter(bill => bill.gst_type === selectedGst);
    }

    // Date Range
    if (startDate) {
      result = result.filter(bill => {
        const date = bill.bill_date || bill.created_at;
        return new Date(date) >= new Date(startDate);
      });
    }
    if (endDate) {
      result = result.filter(bill => {
        const date = bill.bill_date || bill.created_at;
        return new Date(date) <= new Date(endDate + 'T23:59:59');
      });
    }

    setFilteredBills(result);
  };

  // Calculations for Filtered Set
  const calculateTotals = () => {
    let total = 0;
    let cgstSgst = 0;
    let igst = 0;

    filteredBills.forEach(bill => {
      const grandTotal = parseFloat(bill.total_amount || 0);
      total += grandTotal;

      if (bill.gst_type === 'instate') {
        const taxable = grandTotal / 1.18;
        const tax = taxable * 0.09;
        cgstSgst += (tax * 2);
      } else if (bill.gst_type === 'outofstate') {
        const taxable = grandTotal / 1.18;
        igst += (taxable * 0.18);
      }
    });

    return {
      revenue: total,
      cgstSgst,
      igst,
      count: filteredBills.length
    };
  };

  const totals = calculateTotals();

  const handlePreviewPDF = (pdfUrl) => {
    if (!pdfUrl) {
      setAlert({ show: true, message: 'PDF document not found for this bill', severity: 'warning' });
      return;
    }
    setPreviewUrl(pdfUrl);
    setOpenPreview(true);
  };

  const handleDownloadPDF = (pdfUrl, billNo) => {
    if (!pdfUrl) {
      setAlert({ show: true, message: 'PDF document not found for this bill', severity: 'warning' });
      return;
    }
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', `bill-${billNo}.pdf`);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleSoftDelete = async (billId, companyId, billNumber) => {
    if (window.confirm(`Are you sure you want to delete invoice #${billNumber}? This will permanently remove the PDF document.`)) {
      try {
        setLoading(true);
        
        // 1. Soft delete row in Supabase DB
        const { error: dbErr } = await supabase
          .from('bills')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', billId);

        if (dbErr) throw dbErr;

        // 2. Delete PDF from Storage bucket 'bills'
        const storagePath = `company_${companyId}/bill-${billNumber}.pdf`;
        const { error: storageErr } = await supabase.storage
          .from('bills')
          .remove([storagePath]);

        if (storageErr) {
          console.warn("Storage deletion warning (file might have been missing):", storageErr);
        }

        setAlert({ show: true, message: `Invoice #${billNumber} successfully deleted.`, severity: 'success' });
        fetchBills();
      } catch (error) {
        console.error("Delete failed:", error);
        setAlert({ show: true, message: error.message || "Failed to delete bill registry", severity: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const exportToCSV = () => {
    if (filteredBills.length === 0) {
      setAlert({ show: true, message: 'No records matching filters to export', severity: 'warning' });
      return;
    }

    const headers = ['Bill Number', 'Company', 'Customer', 'Date', 'GST Type', 'CGST/SGST (18%)', 'IGST (18%)', 'Grand Total (₹)'];
    const rows = filteredBills.map(b => {
      const grandTotal = parseFloat(b.total_amount || 0);
      let cgstSgstVal = 0;
      let igstVal = 0;

      if (b.gst_type === 'instate') {
        const taxable = grandTotal / 1.18;
        cgstSgstVal = (taxable * 0.18).toFixed(2);
      } else if (b.gst_type === 'outofstate') {
        const taxable = grandTotal / 1.18;
        igstVal = (taxable * 0.18).toFixed(2);
      }

      const dateStr = b.bill_date ? format(new Date(b.bill_date), 'yyyy-MM-dd') : format(new Date(b.created_at), 'yyyy-MM-dd');

      return [
        `#${b.bill_number}`,
        companies[b.company_id] || b.company_id,
        b.customer_name,
        dateStr,
        b.gst_type === 'instate' ? 'In State' : b.gst_type === 'outofstate' ? 'Out of State' : 'No GST',
        cgstSgstVal,
        igstVal,
        grandTotal.toFixed(2)
      ];
    });

    const csvContent = "\uFEFF" + [headers.join(','), ...rows.map(e => e.map(val => `"${val}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `billing_report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(val);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" color="text.primary" gutterBottom>
          Bill Ledger Registry
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track and export generated business tax invoices.
        </Typography>
      </Box>

      {alert.show && (
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, show: false })} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      {/* Analytics Summary Cards (Reflects Filtered Set) */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1', width: 48, height: 48 }}>
                <ChartIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  Filtered Revenue ({totals.count} Invoices)
                </Typography>
                <Typography variant="h5" fontWeight="700" color="#0f172a">
                  {formatCurrency(totals.revenue)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', width: 48, height: 48 }}>
                <PreviewIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  CGST & SGST Collected
                </Typography>
                <Typography variant="h5" fontWeight="700" color="#0f172a">
                  {formatCurrency(totals.cgstSgst)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(168, 85, 247, 0.1)', color: '#a855f7', width: 48, height: 48 }}>
                <PreviewIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  IGST Collected
                </Typography>
                <Typography variant="h5" fontWeight="700" color="#0f172a">
                  {formatCurrency(totals.igst)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Search Panel */}
      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', mb: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.01)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search invoices..."
              placeholder="Search by customer, bill number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Company</InputLabel>
              <Select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                label="Company"
              >
                <MenuItem value="all">All Companies</MenuItem>
                {Object.keys(companies).map(id => (
                  <MenuItem key={id} value={id}>{companies[id]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>GST Type</InputLabel>
              <Select
                value={selectedGst}
                onChange={(e) => setSelectedGst(e.target.value)}
                label="GST Type"
              >
                <MenuItem value="all">All GST Modes</MenuItem>
                <MenuItem value="instate">In State</MenuItem>
                <MenuItem value="outofstate">Out of State</MenuItem>
                <MenuItem value="nogst">No GST</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Quick Ranges:
            </Typography>
            <Button size="small" variant="outlined" onClick={() => handleQuickFilter('this_month')} sx={{ borderRadius: 1.5, textTransform: 'none', py: 0.25, px: 1.5, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#6366f1', color: '#6366f1' } }}>This Month</Button>
            <Button size="small" variant="outlined" onClick={() => handleQuickFilter('this_quarter')} sx={{ borderRadius: 1.5, textTransform: 'none', py: 0.25, px: 1.5, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#6366f1', color: '#6366f1' } }}>This Quarter</Button>
            <Button size="small" variant="outlined" onClick={() => handleQuickFilter('this_fy')} sx={{ borderRadius: 1.5, textTransform: 'none', py: 0.25, px: 1.5, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#6366f1', color: '#6366f1' } }}>This FY (Apr-Mar)</Button>
            <Button size="small" variant="outlined" onClick={() => handleQuickFilter('all_time')} sx={{ borderRadius: 1.5, textTransform: 'none', py: 0.25, px: 1.5, borderColor: '#e2e8f0', color: '#94a3b8', '&:hover': { borderColor: '#64748b', color: '#64748b' } }}>All Time</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Invoices Ledger Table */}
      <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.01)' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc', flexWrap: 'wrap', gap: 2 }}>
          {/* Left Side: Pagination dropdown and index indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: '700', textTransform: 'uppercase' }}>
              Show:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              size="small"
              sx={{ height: 32, fontSize: '0.8rem', bgcolor: '#ffffff', minWidth: '95px' }}
            >
              <MenuItem value={10}>10 rows</MenuItem>
              <MenuItem value={25}>25 rows</MenuItem>
              <MenuItem value={50}>50 rows</MenuItem>
            </Select>

            <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: '500', fontSize: '0.85rem' }}>
              {filteredBills.length > 0 ? `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, filteredBills.length)} of ${filteredBills.length}` : '0 of 0'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                sx={{ height: 32, py: 0, px: 1.5, textTransform: 'none', borderColor: '#e2e8f0', color: '#475569', fontSize: '0.8rem', '&:hover': { borderColor: '#cbd5e1' } }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * rowsPerPage >= filteredBills.length}
                sx={{ height: 32, py: 0, px: 1.5, textTransform: 'none', borderColor: '#e2e8f0', color: '#475569', fontSize: '0.8rem', '&:hover': { borderColor: '#cbd5e1' } }}
              >
                Next
              </Button>
            </Box>
          </Box>

          {/* Right Side: CSV Export Button */}
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, fontWeight: '600', height: 32 }}
          >
            Export Ledger CSV
          </Button>
        </Box>

        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: '600', color: '#475569', width: '90px' }}>Invoice #</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Company</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Billed To</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Bill Date</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#475569' }}>GST Mode</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#475569' }}>Generated By</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#475569', width: '160px' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    Loading invoices registry...
                  </TableCell>
                </TableRow>
              ) : filteredBills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No bills matches your criteria.
                  </TableCell>
                </TableRow>
              ) : (
                filteredBills.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((bill) => {
                  const hasPdf = !!bill.pdf_url;
                  return (
                    <TableRow 
                      key={bill.id} 
                      hover
                      sx={{
                        borderLeft: !hasPdf ? '4px solid #f59e0b' : '4px solid transparent',
                        bgcolor: !hasPdf ? '#fffbeb' : 'inherit'
                      }}
                    >
                      <TableCell sx={{ fontWeight: '600', color: '#6366f1', width: '90px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          #{bill.bill_number}
                          {!hasPdf && (
                            <Chip 
                              label="Legacy (No PDF)" 
                              size="small" 
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: '700', bgcolor: '#fef3c7', color: '#d97706' }} 
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: '500', fontSize: '0.85rem' }}>
                        {companies[bill.company_id] || bill.company_id}
                      </TableCell>
                      <TableCell sx={{ fontWeight: '500' }}>
                        {bill.customer_name}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {bill.bill_date ? format(new Date(bill.bill_date), 'dd/MM/yyyy') : format(new Date(bill.created_at), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={
                            bill.gst_type === 'instate' ? 'In State' : 
                            bill.gst_type === 'outofstate' ? 'Out of State' : 'No GST'
                          } 
                          size="small"
                          sx={{
                            fontWeight: '600',
                            fontSize: '0.75rem',
                            bgcolor: 
                              bill.gst_type === 'instate' ? 'rgba(34, 197, 94, 0.1)' : 
                              bill.gst_type === 'outofstate' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                            color: 
                              bill.gst_type === 'instate' ? '#15803d' : 
                              bill.gst_type === 'outofstate' ? '#6d28d9' : '#475569'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: '700', color: '#0f172a' }}>
                        {formatCurrency(bill.total_amount)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: '500', color: 'text.secondary', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        {bill.generated_by ? (bill.generated_by.length > 10 ? `${bill.generated_by.substring(0, 10)}...` : bill.generated_by) : 'N/A'}
                      </TableCell>
                      <TableCell align="center" sx={{ width: '160px' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handlePreviewPDF(bill.pdf_url)}
                            disabled={!hasPdf}
                            title={hasPdf ? "Preview PDF" : "PDF Unavailable (Legacy)"}
                          >
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleDownloadPDF(bill.pdf_url, bill.bill_number)}
                            disabled={!hasPdf}
                            title={hasPdf ? "Download PDF" : "PDF Unavailable (Legacy)"}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="secondary" 
                            onClick={() => onDuplicateBill(bill)}
                            title="Duplicate Bill"
                          >
                            <CloneIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleSoftDelete(bill.id, bill.company_id, bill.bill_number)}
                            title="Delete Bill"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* PDF View Modal Dialog */}
      <Dialog 
        open={openPreview} 
        onClose={() => setOpenPreview(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '85vh', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', p: 2 }}>
          <Typography variant="h6" fontWeight="700">Invoice Document Viewer</Typography>
          <Button onClick={() => setOpenPreview(false)}>Close</Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {previewUrl ? (
            <iframe 
              src={`${previewUrl}#toolbar=0&navpanes=0`} 
              width="100%" 
              height="100%" 
              style={{ border: 'none' }}
              title="Invoice PDF Preview"
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography>Loading preview...</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BillDatabase;
